import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { LcncAppPicker } from './LcncAppPicker.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ReportRow {
  readonly reportId: string;
  readonly reportCode: string;
  readonly reportName: string;
  readonly reportType: string;
  readonly status: string;
}

const REPORT_TYPES = ['', 'TABULAR', 'SUMMARY', 'PIVOT', 'EXCEPTION', 'KPI', 'AUDIT'];
const STATUSES = ['', 'DRAFT', 'TESTING', 'PUBLISHED', 'ARCHIVED'];
const CREATE_TYPES = REPORT_TYPES.filter(Boolean);

/**
 * M28 — Report Builder (§15 / §32). Designs the six report archetypes (tabular,
 * summary, pivot, exception, KPI, audit) over application data. Lists every
 * report_definition (T371) with its archetype and lifecycle status, filterable
 * by report type and status. Supports create → publish → execute, each wired to
 * the guarded /v1/lcnc/reports write API.
 */
export function M28ReportBuilderPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [reportType, setReportType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [applicationId, setApplicationId] = useState('');
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('TABULAR');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (reportType) params.set('reportType', reportType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ReportRow[] }>(`/v1/lcnc/reports${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reportType, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m28.reportBuilder.notAuthorised', { verb, perm })
      : (err as Error).message;

  const runRowAction = async (fn: () => Promise<unknown>, successMsg: string, verb: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(tr('m28.reportBuilder.validationApplicationId')); return; }
    if (!name.trim()) { setActionError(tr('m28.reportBuilder.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ReportRow }>(`/v1/lcnc/reports`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), reportName: name.trim(), reportType: newType },
        token,
      });
      setNotice(tr('m28.reportBuilder.noticeCreated', { code: r.data.reportCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.reportBuilder.verbBuild'), 'REPORT_BUILD'));
    } finally {
      setBusy(false);
    }
  };

  const publish = (r: ReportRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/reports/${r.reportId}/publish`, { method: 'POST', body: {}, token }),
      tr('m28.reportBuilder.noticePublished', { code: r.reportCode }), tr('m28.reportBuilder.verbBuild'), 'REPORT_BUILD',
    );

  const execute = (r: ReportRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/lcnc/reports/${r.reportId}/execute`, { method: 'POST', body: { parameters: {} }, token }),
      tr('m28.reportBuilder.noticeExecuted', { code: r.reportCode }), tr('m28.reportBuilder.verbBuild'), 'REPORT_BUILD',
    );

  return (
    <main aria-label="report-builder">
      <h1>{tr('m28.reportBuilder.title')}</h1>

      <section aria-label="create-report">
        <h2>{tr('m28.reportBuilder.newHeading')}</h2>
        <form aria-label="report-form" onSubmit={(e) => void create(e)}>
          <LcncAppPicker label={tr('m28.reportBuilder.fieldApplicationId')} value={applicationId} onPick={setApplicationId} />
          <label>{tr('m28.reportBuilder.fieldName')}<input aria-label="field-report-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m28.reportBuilder.fieldType')}
            <select aria-label="field-report-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-report" disabled={busy}>{busy ? tr('m28.reportBuilder.creating') : tr('m28.reportBuilder.create')}</button>
        </form>
        {actionError && <p role="alert" aria-label="report-error">{actionError}</p>}
        {notice && <p aria-label="report-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m28.reportBuilder.filterType')}
          <select aria-label="filter-report-type" value={reportType} onChange={(e) => setReportType(e.target.value)}>
            {REPORT_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m28.reportBuilder.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m28.reportBuilder.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m28.reportBuilder.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m28.reportBuilder.loading')}</p> : (
        <>
          <p aria-label="report-count">{tr('m28.reportBuilder.count', { count: rows.length })}</p>
          <table aria-label="report-table">
            <thead><tr><th>{tr('m28.reportBuilder.colCode')}</th><th>{tr('m28.reportBuilder.colName')}</th><th>{tr('m28.reportBuilder.colType')}</th><th>{tr('m28.reportBuilder.colStatus')}</th><th>{tr('m28.reportBuilder.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.reportId}>
                  <td>{r.reportCode}</td><td>{r.reportName}</td><td>{r.reportType}</td><td>{r.status}</td>
                  <td>
                    <button type="button" aria-label={`publish-${r.reportCode}`} onClick={() => void publish(r)}>{tr('m28.reportBuilder.btnPublish')}</button>
                    <button type="button" aria-label={`execute-${r.reportCode}`} onClick={() => void execute(r)}>{tr('m28.reportBuilder.btnExecute')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
